import { useEffect, useState } from "react";

import type { IOrdersEdgesNode } from "../types/api/customer";
import { OrderStatus, OrderStatusButtonType } from "../types/enum";
import { OrderFinancialStatus, OrderFulfillmentStatus } from "../types/generated/storefront.types";
import { hasPreorder, isReturnPeriodValid, orderHasTracking } from "../utils/order";

// Draw 제품일 경우는, OrderStatusButton 컴포넌트를 사용하는 곳에서 막아줌
// PreOrder 상품은 단독으로만 구매 가능함
// TODO: 추후 프리오더 관련 다양한 케이스를 개발하기로 논의됨
const useOrderStatus = (order?: IOrdersEdgesNode | null, isKR?: boolean) => {
  /** 노출할 버튼을 관리하는 state */
  const [activeButton, setActiveButton] = useState<OrderStatusButtonType>(OrderStatusButtonType.NONE);
  /** 주문 상태를 보여주는 텍스트를 관리하는 state */
  const [orderStatus, setOrderStatus] = useState<OrderStatus>(OrderStatus.None);

  const handleOrderStatus = (type: OrderStatusButtonType, text: OrderStatus) => {
    setActiveButton(type);
    setOrderStatus(text);
  };

  useEffect(() => {
    if (!order) return;
    // 환불 가능 기간
    // 1. 프리오더 주문이거나 intl일 경우, 31일
    // 2. 일반 주문의 경우, 14일
    const returnMaxDate = isKR && !hasPreorder(order) ? 14 : 31;

    const isFamilySale = order.isfamilysale?.value === "true";
    const isGiftingOrder = order.customAttributes.some((item) => item.key === "isGifting" && item.value === "true");
    const isGiftingAccepted = order.giftingIsAccepted?.value === "true";

    const returnStatus = order.customAttributes.find((item) => item.key === "returnStatus")?.value || order.returnStatus?.value;

    const orderStatusHandlers: Record<"preOrder" | "regularOrder", Partial<Record<OrderFinancialStatus, () => void>>> = {
      regularOrder: {
        [OrderFinancialStatus.Paid]: () => {
          // OnHold 상태인 경우
          if (order.fulfillmentStatus === OrderFulfillmentStatus.OnHold) {
            if (isGiftingOrder) {
              handleOrderStatus(OrderStatusButtonType.CANCEL, OrderStatus.Gifting_Pending);
              return;
            }

            handleOrderStatus(OrderStatusButtonType.CANCEL, OrderStatus.Completed);
            return;
          }

          // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Unfulfilled) {
            // 주문 금액이 0원 (이벤트성 할인쿠폰)인데 주문 취소 했을 때
            if (order.canceledAt) {
              handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
              return;
            }

            if (isGiftingOrder) {
              handleOrderStatus(
                isGiftingAccepted ? OrderStatusButtonType.NONE : OrderStatusButtonType.CANCEL,
                isGiftingAccepted ? OrderStatus.Gifting_Accepted : OrderStatus.Gifting_Pending,
              );
              return;
            }

            handleOrderStatus(OrderStatusButtonType.CANCEL, OrderStatus.Completed);
            return;
          }

          // 고객이 매장에서 주문 시
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && order.isOffline?.value === "true") {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Completed);
            return;
          }

          // 반품 신청을 했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "requested") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Requested,
            );
            return;
          }

          // 반품 신청이 승인 되었을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "approved") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Approved,
            );
            return;
          }

          // 패밀리세일에 구매한 주문이 배송완료 되었을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && orderHasTracking(order) && isFamilySale) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Shipped);
            return;
          }

          // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && orderHasTracking(order)) {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Shipped,
            );
            return;
          }

          // 1. 고객이 주문 완료 후 비젬에서 주문 확정했을 때
          // 2. 드로우 주문: 당첨된 오프라인 드로우 주문이면서 비젬에서 주문 확정했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Preparing);
          }
        },
        [OrderFinancialStatus.PartiallyPaid]: () => {
          // 반품배송비가 반품신청한 상품의 금액보다 클 경우

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 처리했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "requested") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Requested,
            );
            return;
          }

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 승인이 되었을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "approved") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Approved,
            );
            return;
          }

          // 반품 처리중인 주문이 존재하지 않을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled) {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Refunded,
            );
          }
        },
        [OrderFinancialStatus.PartiallyRefunded]: () => {
          // 고객이 매장에서 구매후 부분환불을 하는 경우가 존재하는지? -> 안한다고 답변 받음

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 신청을 했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "requested") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Requested,
            );
            return;
          }

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 승인이 되었을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "approved") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Approved,
            );
            return;
          }

          // 모든 주문이 반품되었지만 반품배송비때문에 부분환불로 보이는 경우
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "closed") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Partially,
            );
            return;
          }

          // 반품된 라인아이템이 이미 존재하는 경우 추가로 반품 처리했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled) {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Partially,
            );
          }
        },
        [OrderFinancialStatus.Refunded]: () => {
          // 고객 또는 CS팀에서 주문을 취소했을 때
          if (order.canceledAt) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
            return;
          }

          // 모든 제품에 대해 고객이 반품 신청 후, CS팀에서 승인한 뒤 환불 처리까지 완료했을 때
          // 고객이 매장에서 구매후, 환불한 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Refunded);
        },
        [OrderFinancialStatus.Voided]: () => {
          // 결제 승인만 된 상태에서, 실제로 금액이 청구되기 전에 취소되었을때 - Pending 상태에서 취소되었을 때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
        },
        [OrderFinancialStatus.Pending]: () => {
          // 아직 결제가 진행중인 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Pending);
        },
      },
      preOrder: {
        [OrderFinancialStatus.Paid]: () => {
          // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Unfulfilled) {
            handleOrderStatus(OrderStatusButtonType.CANCEL, OrderStatus.Completed);
            return;
          }

          // 반품 신청을 했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "requested") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Requested,
            );
            return;
          }

          // 반품 신청이 승인 되었을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && returnStatus === "approved") {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Return_Approved,
            );
            return;
          }

          // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled && orderHasTracking(order)) {
            handleOrderStatus(
              isReturnPeriodValid(order, returnMaxDate) ? OrderStatusButtonType.RETURN : OrderStatusButtonType.NONE,
              OrderStatus.Completed_Preparing,
            );
            return;
          }

          // 1. 고객이 주문 완료 후 비젬에서 주문 확정했을 때
          // 2. 드로우 주문: 당첨된 오프라인 드로우 주문이면서 비젬에서 주문 확정했을 때
          if (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Scheduled_Preparing);
            return;
          }

          // OnHold 상태인 경우
          if (order.fulfillmentStatus === OrderFulfillmentStatus.OnHold) {
            handleOrderStatus(OrderStatusButtonType.CANCEL, OrderStatus.Scheduled);
          }
        },
        [OrderFinancialStatus.Refunded]: () => {
          // 고객 또는 CS팀에서 주문을 취소했을 때
          if (order.canceledAt) {
            handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
            return;
          }

          // 모든 제품에 대해 고객이 반품 신청 후, CS팀에서 승인한 뒤 환불 처리까지 완료했을 때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Refunded);
        },
        [OrderFinancialStatus.Voided]: () => {
          // 결제 승인만 된 상태에서, 실제로 금액이 청구되기 전에 취소되었을때 - Pending 상태에서 취소되었을 때
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Canceled);
        },
        [OrderFinancialStatus.Pending]: () => {
          // 아직 결제가 진행중인 경우
          handleOrderStatus(OrderStatusButtonType.NONE, OrderStatus.Pending);
        },
      },
    };

    const isPreOrder = order.lineItems.edges.some((item) => item.node.variant?.product.tags.includes("Preorder")) || false;

    // 프리오더 여부 확인
    if (order.financialStatus) {
      const orderType = isPreOrder ? "preOrder" : "regularOrder";
      orderStatusHandlers[orderType][order.financialStatus]?.();
    }
  }, [order]);

  return { activeButton, orderStatus };
};

export default useOrderStatus;
